import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IconButton from '@mui/material/IconButton';
import CustomButton from 'components/CustomButton';
import CustomChipArray from 'components/CustomChipArray';
import ClientSelect from 'components/ClientSelect';
import { InterviewTemplateSelect } from 'components/TemplateSelect';
import { useSnackbar, useLoading } from "contexts";
import { exportTemplateAnalytics } from "services";
import { copyToClipboard } from "utils";
import BatchSelect from 'components/BatchSelect';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import InterviewTemplateMultiSelect from 'components/TemplateMultiSelect';

function ExportTemplateAnalyticsDialog({
    open,
    onClose = () => { },
    onSuccess = () => { },
    type,
    selectedClient
}) {
    const snackbar = useSnackbar();
    const loading = useLoading();
    const [emails, setEmails] = React.useState([]);
    const [client, setClient] = React.useState(null);
    const [batch, setBatch] = React.useState([]);
    const [template, setTemplate] = React.useState(null);
    const [createdAtStart, setCreatedAtStart] = React.useState(null);
    const [createdAtEnd, setCreatedAtEnd] = React.useState(null);
    const [responseUrl, setResponseUrl] = React.useState(false);
    const [allTemplateList, setAllTemplateList] = React.useState([]);

    const handleEmailsChange = (newEmails) => {
        setEmails(newEmails);
    }

    React.useEffect(() => {
        setEmails([]);
        setResponseUrl(false);
        setClient(null);
    }, [open]);

    const filters = React.useMemo(() => {
        return { user: client?._id, batch: batch.map(el => el?._id) };
    }, [batch, client]);

    const handleExportAnalytics = async () => {
        try {
            loading.show();
            if (!client || !emails.length) {
                throw new Error("Please fill all the fields", "error");
            }
            const startDate = createdAtStart || "0000-01-01T00:00:00.000Z";
            const endDate = createdAtEnd || "9999-12-31T23:59:59.999Z";

            const templateIds = template.length > 0 ? template : allTemplateList;
    
            if (templateIds.length === 0) {
                throw new Error("Please select at least one template or use 'Export All templates'.", "error");
            }
    
            let { url } = await exportTemplateAnalytics({
                emails,
                clientId: client._id,
                templateIds: templateIds,
                createdAtRange: {
                    start: startDate,
                    end: endDate
                }
            });
    
            setResponseUrl(url);
        } catch (error) {
            snackbar.showSnackbar(error.message, "error", true);
        } finally {
            loading.hide();
        }
    };
   
    const handleExportAllAnalytics = async () => {
        try {
            loading.show();
            if (!client || !emails.length) {
                throw new Error("Please fill all the fields", "error");
            }
    
            const startDate = createdAtStart || "0000-01-01T00:00:00.000Z";
            const endDate = createdAtEnd || "9999-12-31T23:59:59.999Z";

            const templateIds = allTemplateList.length > 0 ? allTemplateList : template;
    
            if (templateIds.length === 0) {
                throw new Error("Please select at least one template or use 'Export All templates'.", "error");
            }
    
            let { url } = await exportTemplateAnalytics({
                emails,
                clientId: client._id,
                templateIds: templateIds,
                createdAtRange: {
                    start: startDate,
                    end: endDate
                }
            });
    
            setResponseUrl(url);
        } catch (error) {
            snackbar.showSnackbar(error.message, "error", true);
        } finally {
            loading.hide();
        }
    };

    const handleCopyUrlClick = () => {
        copyToClipboard(responseUrl);
        snackbar.showSnackbar("URL copied to clipboard", "success", true);
    }


    const handleTemplateChange = (value) => {
        const templateIds = value.map((el) => el?._id);
        setTemplate(templateIds);
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
        >
            <DialogTitle >
                Export Template analytics for Client
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ minWidth: 500, maxWidth: 900, overflow: "hidden" }}>
                {!responseUrl ? (
                    <>
                        <ClientSelect onChange={setClient} defaultValue={selectedClient?._id} />
                        <br />
                        <BatchSelect onChange={setBatch} client={client?._id} />
                        <br />

                        <Box display={"flex"} flexDirection={"row"} flexWrap={"nowrap"} justifyContent={"space-around"} >
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker label="CreatedAt Begin"
                                    onChange={(value) => {
                                        const dateString = (new Date(value.$d)).toISOString();
                                        setCreatedAtStart(dateString)
                                    }}
                                    sx={{ maxWidth: "48%" }}
                                />
                                <DatePicker
                                    label="CreatedAt End"
                                    sx={{ maxWidth: "48%" }}
                                    onChange={(value) => {
                                        const dateString = (new Date(value.$d)).toISOString();
                                        setCreatedAtEnd(dateString)
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>

                        <br />
                        <InterviewTemplateMultiSelect
                            filters={filters}
                            onChange={handleTemplateChange}
                            setAllTemplateList={setAllTemplateList}
                        />
                        <br />
                        <br />
                        {(client) && (
                            <CustomChipArray
                                InputProps={{
                                    autoFocus: true,
                                    type: 'email',
                                    name: "email",
                                    placeholder: "Add emails for sheet permissions...",
                                }}
                                onValuesChange={handleEmailsChange}
                            />
                        )}
                        <br />
                        <br />
                        {template ? (
                            <CustomButton
                                variant="contained"
                                disabled={emails.length <= 0 || !client}
                                onClick={handleExportAnalytics}
                            >
                                Export
                            </CustomButton>
                        ) : (
                            <CustomButton
                                variant="contained"
                                disabled={emails.length <= 0 || !client}
                                onClick={handleExportAllAnalytics}
                            >
                                Export All templates
                            </CustomButton>
                        )}

                    </>
                ) : (
                    <Box
                        border="1px solid lightgrey"
                        my="1em"
                        p="1em"
                        display="flex"
                        alignItems="center"
                    >
                        <Box flexGrow={1} overflow="hidden">
                            <Typography
                                variant="subtitle1"
                                color="blue"
                                component="a"
                                sx={{ wordWrap: "break-word" }}
                                href={responseUrl}
                                target={"_blank"}
                            >
                                {responseUrl}
                            </Typography>
                        </Box>
                        <Box ml={1}>
                            <IconButton variant="outlined">
                                <ContentCopyIcon
                                    sx={{ cursor: "pointer" }}
                                    onClick={handleCopyUrlClick}
                                />
                            </IconButton>
                        </Box>
                    </Box>
                )}
                <CustomButton
                    variant="outlined"
                    onClick={onClose}
                >
                    Cancel
                </CustomButton>
            </DialogContent>
        </Dialog>
    );
}

export default ExportTemplateAnalyticsDialog;